// cleanup_strings_PYMTCNTR
// cleanup_hoc_to_hooks_PYMTCNTR
// cleanup_remove_external_libraries_PYMTCNTR
// cleanup_convert_to_typescript_file_PYMTCNTR

import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import moment from 'moment';
import numeral from 'numeral';

import Loader from 'components/loader/Loader';
import {
  transformColumns,
  buildColumnState
} from 'components/basicMobileGrid/BasicMobileGrid.utils';
import BasicMobileGrid from 'components/basicMobileGrid/BasicMobileGrid';
import ChallengeManager from 'components/challengeManager/ChallengeManager';
import withRouterConnect from 'components/withRouterConnect/withRouterConnect';
import useLocale from 'components/useLocale';
import { PaymentActionAlertsComponent as PaymentActionAlerts } from 'components/paymentActionAlerts/PaymentActionAlerts';
import { actionPayments } from 'api/payments/service';
import { getSmallBusinessPayments, getPermissions } from 'api/smallBusinessPayments/service';
import { setPaymentActionMessages } from 'logic/store/payments/actions';
import { ACTIONS, GRID_IDS } from 'constants.js';

import SmallBusinessPaymentsNative from '../../smallBusinessPayments/native/SmallBusinessPayments';
import getSuccessfulDetails, { getFailedDetails } from '../web/paymentConfirmation.utils';
import { staticViews } from '../web/payments.staticViews';
import { aggregatePayments } from '../web/payments.utils';
import { PAYMENTS_COLUMNS as columnDefs } from '../web/payments.constants';
import { paymentsColumns } from '../payments.columns';
import PaymentsDataProvider from '../PaymentsDataProvider';

const SmallBusinessPayments = () => {
  const [smbPaymentsData, setSmbPaymentsData] = React.useState();
  const [refresh, setRefresh] = React.useState(false);
  useEffect(() => {
    // THIS IS YOUR API CALLS
    const subscription = getSmallBusinessPayments().subscribe((smbPaymentsData) => {
      setSmbPaymentsData(smbPaymentsData);
    });
    return () => subscription.unsubscribe();
  }, [setSmbPaymentsData, refresh]);
  return smbPaymentsData ? (
    <SmallBusinessPaymentsNative
      smbPaymentsData={smbPaymentsData}
      refresh={() => setRefresh(new Date())}
      key={`small-business-${refresh}`}
    />
  ) : (
    <Loader />
  );
};

export const PaymentsNativeComponent = ({ setPaymentActionMessages }) => {
  const locale = useLocale();
  const serverTimezone = useSelector((state) => state?.auth?.user?.timeZone);

  const [challengeManager, setChallengeManager] = useState(false);
  const [challengeErrors, setChallengeErrors] = useState(false);
  const [challengeHandler, setChallengeHandler] = useState(() => {});
  const SBPermissions = [
    'SBRegPymt',
    'SBExpPymt',
    'SBColPymt',
    'SBTaxPymt',
    'SBLNPymt',
    'SBLNDraw',
    'SBEmpPymt',
    // TODO: show view only if this is true
    'SBVOEmp',
    'SBPayApp'
  ];

  const [smallBusinessPermissions, setSmallBusinessPermissions] = useState();

  useEffect(() => {
    const subscription = getPermissions(SBPermissions)().subscribe((data) => {
      setSmallBusinessPermissions(Object.values(data).some(Boolean));
    });
    return () => subscription.unsubscribe();
  }, [SBPermissions]);

  const onActionClick =
    (updates, action) =>
    ({ rowData: payment }) => {
      const payload = [payment].map((payment) => ({
        action,
        effectiveDate: moment(payment.effectiveEntryDate),
        paymentCategory: payment.paymentCategory,
        ...(payment.custTrace && { custTrace: payment.custTrace }),
        ...(payment.bankTrace && { bankTrace: payment.bankTrace }),
        corpPayment: true
      }));

      return Promise.resolve({ asPromise: true, paymentActionRequest: payload })
        .then(actionPayments)
        .then(({ data }) => {
          // Handle challenge manager
          if (
            (data && data.challeangeable === true) ||
            (data && Array.isArray(data) && data[0].code === 'ACCOUNT_LOCKED')
          ) {
            setChallengeErrors(Boolean(data.numberOfFailedAttempts));
            setChallengeHandler(() => () => {
              onActionClick(updates, action)({ rowData: payment });
            });
            if (!challengeManager) {
              return setChallengeManager(data);
            }
          } else {
            setChallengeManager(false);
            setChallengeErrors(false);
            setChallengeHandler(() => () => {});
          }

          // // Format object of objects to an array
          const paymentsResponse = Object.values(data);
          // Format success and failure payments based on ACH / Wire / and Action
          const successfulPayments = paymentsResponse
            .filter((response) => response.success === true)
            .map(({ payment }) => getSuccessfulDetails({ action, locale, payment }));
          const failedPayments = paymentsResponse
            .filter((response) => response.success === false)
            .map(({ payment, reason }) =>
              getFailedDetails({
                action,
                locale,
                payment,
                reason
              })
            );

          // Check for successful and failed payments before creating alerts for them
          setPaymentActionMessages([
            ...(successfulPayments.length > 0
              ? [
                  {
                    details: successfulPayments,
                    id: 1,
                    success: true,
                    title: `${successfulPayments.length} ${
                      successfulPayments.length > 1
                        ? locale['common.payments']
                        : locale['common.payment']
                    } ${locale['common.succeeded']} ${locale['common.action']} ${
                      action && `(${locale[`common.${action.toLowerCase()}`]})`
                    }`
                  }
                ]
              : []),
            ...(failedPayments.length > 0
              ? [
                  {
                    details: failedPayments,
                    id: 2,
                    success: false,
                    title: `${failedPayments.length} ${
                      failedPayments.length > 1
                        ? locale['common.payments']
                        : locale['common.payment']
                    } ${locale['common.failed']} ${locale['common.action']} ${
                      action && `(${locale[`common.${action.toLowerCase()}`]})`
                    }`
                  }
                ]
              : [])
          ]);

          if (successfulPayments.length > 0) {
            updates.updatePermissions();
            updates.updateData();
          }
          return false;
        });
    };

  /**
   * Comparator to be used for action visibility
   * @param {object} props
   * @param {object} props.permissions object with permissions
   * @param {string} props.permissions action we should be targeting against
   * @param {array<string>} props.possibleActions action we should be targeting against
   */
  const rowActionComparator =
    ({ action, permissions }) =>
    ({ possibleActions }) => {
      const permission = action.toLowerCase();
      return (
        permissions[permission] && possibleActions.map((x) => x.toLowerCase()).includes(permission)
      );
    };

  return (
    <>
      <PaymentsDataProvider>
        {({ data, hasCorporatePermissions, permissions, updates }) => {
          const primary = ['payeeName', 'amount', 'status'];
          const exclude = ['selector', 'possibleActions'];
          const webColumns = paymentsColumns({ locale });

          const getFormatedData = aggregatePayments({ data, serverTimezone });

          // prepare grid required data
          const columns = transformColumns({
            columns: webColumns,
            data,
            primary
          }).map((col) => {
            if (['payeeName'].includes(col.colId)) {
              return { ...col, variant: 'fuzzy' };
            }
            return col;
          });
          const columnState = buildColumnState(primary);
          if (!hasCorporatePermissions && smallBusinessPermissions) {
            return (
              <>
                <PaymentActionAlerts paymentsGrid />
                <SmallBusinessPayments />
              </>
            );
          }

          return (
            <>
              <PaymentActionAlerts paymentsGrid />
              <BasicMobileGrid
                gridId={GRID_IDS.PAYMENTS_GRID}
                columnDefs={columns}
                webColumns={webColumns}
                columnState={columnState}
                records={getFormatedData}
                exclude={exclude}
                savedViews={staticViews({ columnDefs: webColumns, locale })}
                localeText={{ noRowsToShow: locale['common.grid.noRecordsFound'] }}
                getRowActions={[
                  {
                    condition: rowActionComparator({ action: ACTIONS.APPROVE, permissions }),
                    id: 'approve',
                    label: locale['common.approve'],
                    onClick: onActionClick(updates, ACTIONS.APPROVE)
                  },
                  {
                    condition: rowActionComparator({ action: ACTIONS.REJECT, permissions }),
                    id: 'reject',
                    label: locale['common.reject'],
                    onClick: onActionClick(updates, ACTIONS.REJECT)
                  },
                  {
                    condition: rowActionComparator({ action: ACTIONS.DELETE, permissions }),
                    id: 'delete',
                    label: locale['common.delete'],
                    onClick: onActionClick(updates, ACTIONS.DELETE)
                  }
                ]}
                formatters={{
                  fields: [
                    {
                      field: columnDefs.amount,
                      format: ({ value }) => numeral(value).format('0,0.00')
                    }
                  ],
                  valueFallback: locale['common.grid.empty']
                }}
              />
            </>
          );
        }}
      </PaymentsDataProvider>
      {challengeManager && (
        <ChallengeManager
          challengeErrorMessage={challengeErrors}
          onCancel={() => setChallengeManager(false)}
          onHide={() => setChallengeManager(false)}
          data={challengeManager}
          onSubmit={() => {
            challengeHandler();
          }}
          open
        />
      )}
    </>
  );
};

PaymentsNativeComponent.propTypes = {
  setPaymentActionMessages: PropTypes.func.isRequired
};

const mapDispatchToProps = (dispatch) => ({
  setPaymentActionMessages: (paymentActionMessages) =>
    dispatch(setPaymentActionMessages(paymentActionMessages))
});

export default withRouterConnect(null, mapDispatchToProps)(PaymentsNativeComponent);
